/**
 * One chat bubble — simplified from aurora's chat MessageWrapper/TextContent
 * (aurora-template/src/components/sections/chat/.../message/) for the
 * two-party user↔assistant thread: user right/primary, assistant left/
 * elevation2, with aurora's consecutive-message radius tightening.
 *
 * A column wrapper (not the Paper) owns the left/right alignment so an optional
 * identity header (avatar + name + time, at the start of a run) sits above the
 * bubble and an optional `footer` sits directly under it, both aligned to it.
 * Assistant text is markdown-light; user text is verbatim.
 */

import { ReactNode } from 'react';
import { Paper, Stack, Typography } from '@mui/material';
import AssistantMarkdown from './AssistantMarkdown';
import AssistantMessageHeader from './AssistantMessageHeader';

interface AssistantMessageBubbleProps {
  role: 'user' | 'assistant';
  content: string;
  /** Role of the previous message — consecutive same-role bubbles round evenly. */
  prevRole?: 'user' | 'assistant';
  /** Show the avatar + name + time header (start of a consecutive same-sender run). */
  showHeader?: boolean;
  /** ISO timestamp for the header; omitted while streaming. */
  createdAt?: string;
  /** Current couple's identity, for the header on their own turns. */
  userName?: string;
  userAvatar?: string | null;
  /** Optional row rendered under the bubble, aligned to it. */
  footer?: ReactNode;
}

const AssistantMessageBubble = ({
  role,
  content,
  prevRole,
  showHeader,
  createdAt,
  userName,
  userAvatar,
  footer,
}: AssistantMessageBubbleProps) => {
  const isUser = role === 'user';

  return (
    <Stack
      direction="column"
      spacing={0.75}
      sx={{
        alignSelf: isUser ? 'flex-end' : 'flex-start',
        alignItems: isUser ? 'flex-end' : 'flex-start',
        maxWidth: '85%',
      }}
    >
      {showHeader && (
        <AssistantMessageHeader
          role={role}
          createdAt={createdAt}
          userName={userName}
          userAvatar={userAvatar}
        />
      )}
      <Paper
        sx={{
          outline: 'none',
          position: 'relative',
          borderRadius: (theme) => {
            if (prevRole === role) return theme.spacing(2, 2, 2, 2);
            return isUser ? theme.spacing(2, 0, 2, 2) : theme.spacing(0, 2, 2, 2);
          },
          width: 'fit-content',
          maxWidth: '100%',
          bgcolor: isUser ? 'primary.main' : 'background.elevation2',
          px: 2,
          py: 1.5,
          color: isUser ? 'primary.contrastText' : 'text.primary',
          // Trim the outer margins of the first/last markdown blocks so the
          // bubble padding stays even.
          '& > :first-of-type': { mt: 0 },
          '& > :last-of-type': { mb: 0 },
        }}
      >
        {isUser ? (
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'break-word' }}>
            {content}
          </Typography>
        ) : (
          <AssistantMarkdown content={content} />
        )}
      </Paper>
      {footer}
    </Stack>
  );
};

export default AssistantMessageBubble;
